import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate, timeAgo } from "@/lib/format";
import { Handshake, ChatCircleText, ArrowRight, Compass } from "@phosphor-icons/react";

const StatusBadge = ({ s }) => {
  const map = {
    new: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    in_progress: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    awaiting_response: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    resolved: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    closed: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    // legacy fallbacks (in case old rows leak through)
    pending: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    open: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    scheduled: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    replied: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    declined: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  };
  const label = (s || "new").replace(/_/g, " ");
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${map[s] || map.new}`}>{label}</span>;
};

export default function MyRequests() {
  const [meetings, setMeetings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  useEffect(() => {
    api.get("/meetings").then((r) => setMeetings(r.data));
    api.get("/enquiries").then((r) => setEnquiries(r.data));
  }, []);
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Activity</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">My requests</h1>
      </div>
      <Tabs defaultValue="meetings">
        <TabsList>
          <TabsTrigger value="meetings" data-testid="requests-tab-meetings"><Handshake size={14} className="mr-1.5" /> Meetings ({meetings.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="requests-tab-enquiries"><ChatCircleText size={14} className="mr-1.5" /> Enquiries ({enquiries.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="meetings" className="mt-4">
          {meetings.length === 0 ? (
            <RequestsEmptyState
              icon={Handshake}
              heading="No meeting requests yet"
              body="Once you request a call with a company from its profile, you&apos;ll see the status and history here."
              ctaLabel="Browse companies"
              testid="empty-meetings"
            />
          ) : (
            <div className="space-y-2">
              {meetings.map((m) => (
                <Link to={`/companies/${m.company_id}`} key={m.id} className="block rounded-lg border border-border bg-card p-4 hover:shadow-sm transition-shadow" data-testid={`meeting-item-${m.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded bg-secondary overflow-hidden">
                      {m.company?.logo_url && <img src={m.company.logo_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{m.company?.name}</span>
                        <StatusBadge s={m.status} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{m.preferred_date ? `Preferred: ${formatDate(m.preferred_date)}` : "Flexible"} · {timeAgo(m.created_at)}</div>
                      {m.message && <div className="text-sm text-muted-foreground mt-1 truncate">{m.message}</div>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="enquiries" className="mt-4">
          {enquiries.length === 0 ? (
            <RequestsEmptyState
              icon={ChatCircleText}
              heading="No enquiries yet"
              body="Contact a company directly from its profile — your questions and their replies will appear here."
              ctaLabel="Browse companies"
              testid="empty-enquiries"
            />
          ) : (
            <div className="space-y-2">
              {enquiries.map((e) => (
                <Link to={`/companies/${e.company_id}`} key={e.id} className="block rounded-lg border border-border bg-card p-4 hover:shadow-sm transition-shadow" data-testid={`enquiry-item-${e.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded bg-secondary overflow-hidden">
                      {e.company?.logo_url && <img src={e.company.logo_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{e.company?.name}</span>
                        <StatusBadge s={e.status} />
                      </div>
                      <div className="text-sm font-medium mt-0.5">{e.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{timeAgo(e.created_at)}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RequestsEmptyState({ icon: Icon, heading, body, ctaLabel, testid }) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-16 text-center" data-testid={testid}>
      <div className="h-14 w-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 grid place-items-center mx-auto mb-4">
        <Icon size={26} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
      </div>
      <div className="font-display font-bold text-foreground text-lg">{heading}</div>
      <div className="text-sm mt-2 text-muted-foreground max-w-md mx-auto">{body}</div>
      <Link
        to="/"
        data-testid={`${testid}-cta`}
        className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white text-sm font-semibold transition-colors"
      >
        <Compass size={15} /> {ctaLabel} <ArrowRight size={15} />
      </Link>
    </div>
  );
}
